<template>
   <timeline-element-container secondary blue :key="`${reportOfSickness.id}-${period.id}-tec`">
      <div slot='left' class='date-container'>
        <p>{{ formatDate(period.startDate) }}</p>
        <p class='label'>{{ 'general.report-on' | translate }}</p>
        <p>{{ formatDate(period.reportDate) }}</p>
      </div>

      <div slot='right' class='report-detail'>
        <div class='fl hf mb-4'>
          <header-element gray :class="isDeletable() || isEditable() ? 'mr-2' : ''">
            {{ 'period.general.change-in-absence' | translate }}
          </header-element>

          <header-element-button gray class='mr-2' :disabled="isCurrentlyProcessing"
            @click="handleEditPeriod"
            v-if="isEditable()">
            <v-icon>edit</v-icon>
          </header-element-button>

          <header-element-button gray remove :disabled="isCurrentlyProcessing"
            @click="handleDeletePeriod"
            v-if="isDeletable()">
            <v-icon v-if="!isDeletePeriodLoading">delete</v-icon>
            <v-progress-circular v-else :size="18" :width="2.8" indeterminate />
          </header-element-button>
        </div>

        <div class='fl element-details'>
          <disability-rate-difference
            :current="period.disabilityRate"
            :previous="prevDisabilityRate"
            class='disability-rate-difference' />

          <div class='hf'>
            <table class='confirmation-table'>
              <tr>
                <td>{{ 'report-of-sickness.safetynet' | translate }}</td>
                <td>{{ reportOfSickness.safetyNet | translate }}</td>
              </tr>
              <tr>
                <td>{{ 'report-of-sickness.accident' | translate }}</td>
                <td>{{ reportOfSickness.accident | translate }}</td>
              </tr>
              <tr v-if="reportOfSickness.thirdPartyLiability != null">
                <td>{{ 'report-of-sickness.third-party-liability' | translate }}</td>
                <td>{{ reportOfSickness.thirdPartyLiability | translate }}</td>
              </tr>
              <tr v-if="isStatusVisible">
                <td>Status</td>
                <td>
                  <report-of-sickness-status :status="period.status" />
                </td>
              </tr>
            </table>
          </div>
        </div>
      </div>
    </timeline-element-container>
</template>

<script>
import { mapGetters } from 'vuex';

import Modal from '@/shared/constants/modal';
import Helpers from '@/shared/helpers/functions';

import ReportOfSicknessStatus from '@/views/components/report-of-sickness-details/ReportOfSicknessStatus.vue';
import TimelineElementContainer from '@aevitae/aevitaeui/0_atoms/containers/TimelineElementContainer.vue';
import DisabilityRateDifference from '@/views/components/report-of-sickness-details/DisabilityRateDifference.vue';
import HeaderElement from '@aevitae/aevitaeui/0_atoms/elements/HeaderElement.vue';
import HeaderElementButton from '@aevitae/aevitaeui/0_atoms/elements/HeaderElementButton.vue';

import ReportsOfSickness from '@/views/constants/reports-of-sickness';

import { Period } from '@/resources/models/period';
import States from '@/resources/models/states';

export default {
  name: 'PeriodTimelineElement',
  components: {
    TimelineElementContainer,
    ReportOfSicknessStatus,
    DisabilityRateDifference,
    HeaderElement,
    HeaderElementButton
  },
  props: {
    reportOfSickness: Object,
    period: Object,
    employee: Object,
    prevDisabilityRate: null
  },
  computed: {
    ...mapGetters({
      isDeletePeriodLoading: 'isDeletePeriodLoading',
      isCurrentlyProcessing: 'isCurrentlyProcessing'
    }),
    isLatest () {
      let sortedPeriods = this.reportOfSickness.periods.slice().sort((a, b) => {
        if (new Date(a.startDate) > new Date(b.startDate)) {
          return -1;
        } else if (new Date(b.startDate) > new Date(a.startDate)) {
          return 1;
        } else {
          return 0;
        }
      });
      return this.period.id === sortedPeriods[0].id;
    },
    isStatusVisible () {
      return [States.Rejected, States.ToReview].indexOf(this.period.status) > -1;
    }
  },
  methods: {
    formatDate: (date) => Helpers.Dates.formatDate(date),

    isDeletable () {
      const isViableStatus = (
        this.period.status === States.Approved ||
        this.period.status === States.Processed ||
        this.period.status === States.Rejected
      );
      const isRecovered = this.reportOfSickness.recovery != null;

      return this.isLatest && isViableStatus && !isRecovered;
    },

    isEditable () {
      const isViableStatus = (
        this.period.status === States.Approved ||
        this.period.status === States.Processed
      );
      const isRecovered = this.reportOfSickness.recovery != null;

      return this.isLatest && isViableStatus && !isRecovered;
    },

    handleEditPeriod (period) {
      if (this.isCurrentlyProcessing) {
        this.$store.dispatch(Modal.Actions.OPEN, {
          component: 'Error',
          title: this.$t('period.modals.edit-currently-processing-period-title'),
          text: this.$t('period.modals.edit-currently-processing-period-text')
        });
      } else if (this.period.status === States.Processed) {
        this.$store.dispatch(Modal.Actions.OPEN, {
          component: 'EditProcessedPeriodConfirmation',
          reportOfSickness: this.reportOfSickness,
          employee: this.employee,
          period: this.period
        });
      } else {
        this.$store.dispatch(
          ReportsOfSickness.Actions.Tabs.OPEN_PERIOD_EDIT, {
            reportOfSickness: this.reportOfSickness,
            period: Object.assign(new Period(), this.period)
          }
        );
      }
    },

    handleDeletePeriod () {
      let options = {};
      if (this.isCurrentlyProcessing) {
        options = {
          component: 'Error',
          title: this.$t('period.modals.delete-currently-processing-period-title'),
          text: this.$t('period.modals.delete-currently-processing-period-text')
        };
      } else if (this.period.status === States.Processed) {
        options = {
          component: 'DeleteProcessedPeriodConfirmation',
          reportOfSickness: this.reportOfSickness,
          employee: this.employee,
          period: this.period
        };
      } else {
        options = {
          component: 'DeletePeriodConfirmation',
          reportOfSickness: this.reportOfSickness,
          employee: this.employee,
          period: this.period
        };
      }
      this.$store.dispatch(Modal.Actions.OPEN, options);
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/stylesheets/variables.scss';

h2 {
  color: $primary;
}

h5 {
  font-size: 1.4em;
  letter-spacing: -.1px;
  font-family: 'Source Sans Pro';
}

h6 {
  position: relative;
  top: 1px;
}

.element-details {
  padding: 0px 15px;
}

.disability-rate-difference {
  position: relative;
  top: 2px;
  margin-right: 25px;
}

.confirmation-table {
  tr {
    &:first-child {
      td {
        border-top: 0;
        padding-top: 0;
      }
    }
    &:last-child {
      td {
        border-bottom: 0;
        padding-bottom: 0;
      }
    }
  }
}

.confirmation-table-adjust-padding {
  td {
    &:first-child {
      padding-left: 14px;
    }
    &:last-child {
      padding-right: 14px;
    }
  }
}

.report-detail {
  position: relative;
  top: -10px;
  margin-bottom: 30px;
}

.date-container {
  :nth-child(1) {
    font-weight: 600;
    color: $primary;
    font-size: 1.1em;
    margin-bottom: 10px;

    &.recovery { color: $positive; }
  }
}

.recovery {
  color: $positive;
}

.action-list {
  background-color: $secondary-lighten-3;
  padding: 30px;

  h3 {
    font-size: 1em;
    color: $secondary;
    margin-bottom: 20px;
  }

  .item {
    background-color: white;
    padding: 5px;

    p {
      color: $primary;
    }
  }
}

</style>
